import { useEffect, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
    Button,
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
    Switch,
} from "@material-tailwind/react";
import RHFInput from "../form/rhf-input";
import RHFTextarea from "../form/rhf-textarea";

export default function QuestionSettingsDialog({
    showDialog,
    qI,
    handleClose,
    isReadOnly,
}) {
    const { getValues, setValue } = useFormContext();
    const index = qI || 0;
    // Render-time snapshot of the questions array: the dialog is modal,
    // but not isolated from the store — an SWR re-delivery can fire the
    // parent's P5 reset while the dialog is open. The exposure is
    // bounded: the reset re-seeds membership-stable rows at the same
    // indices, and Apply writes read local dialog state plus live
    // setValue calls, so no stale snapshot ships.
    const questions = getValues("questions");
    const [questionDescription, showQuestionDescription] = useState(false);
    const [questionDemographic, showQuestionDemographic] = useState(false);
    const [questionPII, showQuestionPII] = useState(false);
    const [respondentsSeeOthers, showRespondentsSeeOthers] = useState(false);
    const [showPresetRemovalDialog, setShowPresetRemovalDialog] =
        useState(false);
    const [pendingType, setPendingType] = useState(null);
    const [answerType, setAnswerType] = useState("multiple");

    const updateStates = useCallback(() => {
        const currentQuestions = getValues("questions");
        showQuestionDescription(
            Boolean(currentQuestions?.[index]?.description),
        );
        showRespondentsSeeOthers(
            Boolean(currentQuestions?.[index]?.is_view_all),
        );
        showQuestionDemographic(
            Boolean(currentQuestions?.[index]?.is_demographic_question),
        );
        showQuestionPII(Boolean(currentQuestions?.[index]?.contains_pii));
        setAnswerType(currentQuestions?.[index]?.answer_type || "multiple");
    }, [index, getValues]);

    useEffect(() => {
        updateStates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDialog]);
    const handleDialogClose = (value = false) => {
        setPendingType(null);
        handleClose(value);
    };

    // setValue wrapper with the setFieldValue(name, value) call shape.
    const setFieldValue = (name, value) =>
        setValue(name, value, { shouldDirty: true, shouldValidate: true });

    const handleCheckDescription = () => {
        showQuestionDescription(!questionDescription);
    };

    const handleQuestionDemographic = () => {
        showQuestionDemographic(!questionDemographic);
    };

    const handleQuestionPII = () => {
        const newPIIValue = !questionPII;
        showQuestionPII(newPIIValue);
        if (newPIIValue) {
            // PII enabled: matched-responses must be off.
            showRespondentsSeeOthers(false);
        }
    };

    const handleCheckViewOthers = () => {
        // Matched responses are blocked while PII is enabled.
        if (questionPII && !respondentsSeeOthers) {
            return;
        }
        showRespondentsSeeOthers(!respondentsSeeOthers);
    };
    const closeOnly = () => {
        setPendingType(null);
        setShowPresetRemovalDialog(false);
    };
    const onConfirmDeletion = () => {
        setFieldValue(`questions.${index}.preset_answers`, []);
        setFieldValue(`questions.${index}.question_type`, "freeform");
        setShowPresetRemovalDialog(false);
        setPendingType(null);
    };
    const handleApply = () => {
        if (pendingType === "freeform") {
            setShowPresetRemovalDialog(true);
        } else if (pendingType) {
            setFieldValue(`questions.${index}.question_type`, pendingType);
            if (pendingType === "mixed" || pendingType === "fixed") {
                // Mixed/Fixed questions never show respondents others' answers.
                setFieldValue(`questions.${index}.is_view_all`, false);
            }
            setPendingType(null);
        }
        if (!questionDescription) {
            setFieldValue(`questions.${index}.description`, "");
        }
        setFieldValue(
            `questions.${index}.is_demographic_question`,
            questionDemographic,
        );
        setFieldValue(`questions.${index}.contains_pii`, questionPII);
        // Matching only applies to freeform questions; PII also disables it.
        const isFreeform =
            pendingType === "freeform" ||
            (!pendingType && questions?.[index]?.question_type === "freeform");
        setFieldValue(
            `questions.${index}.is_view_all`,
            (questionPII || !isFreeform) ? false : respondentsSeeOthers,
        );
        setFieldValue(`questions.${index}.answer_type`, answerType);
        handleDialogClose(false);
    };

    return (
        <>
            <Dialog
                open={showDialog}
                handler={handleDialogClose}
                className="dialog"
                size="lg"
                animate={{
                    mount: { scale: 1, y: 0 },
                    unmount: { scale: 1, y: -100 },
                }}
            >
                <DialogHeader className="dialog__header">
                    Question Settings
                </DialogHeader>
                <DialogBody className="dialog__body">
                    <div className="flex flex-col gap-2 flex-auto">
                        <div className="flex flex-col md:flex-row md:items-start gap-4 border border-gray-400 p-4 rounded-xl">
                            <div className="flex-1">
                                <Typography variant="h5" className="text-black">
                                    Question Type
                                </Typography>
                                <Typography variant="paragraph" color="black">
                                    This sets the basic question type. There are
                                    3 variations. 1. Gobby&#8482; - Respondents
                                    write in their own words 2. Pre-set - Add
                                    one or more response options that people can
                                    choose. This is good for demographic style
                                    questions. 3. Mixed - Allows for both
                                    Pre-set and Gobby response options. (Note:
                                    All pre-set responses are excluded from
                                    &lsquo;Gobby Assist&rsquo; analysis)
                                </Typography>
                            </div>

                            <div className="flex flex-col gap-2 flex-shrink-0 md:place-self-center">
                                <Button
                                    type="button"
                                    className={`btn btn--cta border ${
                                        pendingType === "freeform" ||
                                        (!pendingType &&
                                            questions?.[index]
                                                ?.question_type === "freeform")
                                            ? "border-cta"
                                            : "!bg-white border-black"
                                    }`}
                                    size="sm"
                                    onClick={() => {
                                        setPendingType("freeform");
                                    }}
                                    disabled={isReadOnly}
                                >
                                    Gobby
                                </Button>
                                <Button
                                    type="button"
                                    className={`btn btn--cta border ${
                                        pendingType === "mixed" ||
                                        (!pendingType &&
                                            questions?.[index]
                                                ?.question_type === "mixed")
                                            ? "border-cta"
                                            : "!bg-white border-black"
                                    }`}
                                    size="sm"
                                    onClick={() => {
                                        setPendingType("mixed");
                                    }}
                                    disabled={isReadOnly}
                                >
                                    Mixed
                                </Button>
                                <Button
                                    type="button"
                                    className={`btn btn--cta border ${
                                        pendingType === "fixed" ||
                                        (!pendingType &&
                                            questions?.[index]
                                                ?.question_type === "fixed")
                                            ? "border-cta"
                                            : "!bg-white border-black"
                                    }`}
                                    size="sm"
                                    onClick={() => {
                                        setPendingType("fixed");
                                    }}
                                    disabled={isReadOnly}
                                >
                                    Pre-set
                                </Button>
                            </div>
                        </div>

                        <div className="flex justify-between border border-gray-400 p-2 border-1 rounded-md">
                            <div className="basis-5/6">
                                <Typography
                                    variant="h5"
                                    className="text-black"
                                >
                                    Question collects sensitive or Personally Identifiable
                                    Information (PII)
                                </Typography>
                                <Typography
                                    variant="paragraph"
                                    color="black"
                                >
                                    Use this toggle to track if responses will
                                    contain sensitive or Personally Identifiable
                                    Information (PII). Please ensure you have
                                    informed survey recipients of this in
                                    accordance with data protection laws.
                                    These responses are excluded from
                                    &lsquo;Gobby Assist&rsquo; analysis.
                                </Typography>
                            </div>
                            <div className="place-self-center">
                                <Switch
                                    checked={questionPII}
                                    color="orange"
                                    id="checkbox6"
                                    text=""
                                    onChange={() => handleQuestionPII()}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>

                        {!!(
                            pendingType === "mixed" ||
                            pendingType === "fixed" ||
                            (!pendingType && questions?.[index]?.question_type === "mixed") ||
                            (!pendingType && questions?.[index]?.question_type === "fixed")
                        ) && (
                            <div className="flex justify-between border border-gray-400 p-2 border-1 rounded-md">
                                <div className="basis-5/6">
                                    <Typography variant="h5" className="text-black">
                                        Attribute Question
                                    </Typography>
                                    <Typography variant="paragraph" color="black">
                                        The question defines attributes that can
                                        be categorised. These attributes can be
                                        related to respondents (i.e. demographics),
                                        products (e.g. features), or services
                                        (e.g. types). Attribute questions improve
                                        data analysis by comparing them with
                                        qualitative themes, making patterns clearer
                                        and insights more meaningful.
                                    </Typography>
                                </div>
                                <div className="place-self-center">
                                    <Switch
                                        checked={questionDemographic}
                                        color="orange"
                                        id="checkbox5"
                                        text=""
                                        onChange={() => handleQuestionDemographic()}
                                        disabled={isReadOnly}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col md:flex-row md:items-start gap-4 border border-gray-400 p-2 rounded-md">
                            <div className="flex-1">
                                <Typography variant="h5" className="text-black">
                                    Single or multiple responses{" "}
                                </Typography>
                                <Typography variant="paragraph" color="black">
                                    Allow respondents to add a Single or
                                    Multiple responses to this question.
                                </Typography>
                            </div>
                            <div className="flex flex-col gap-2 flex-shrink-0 md:place-self-center">
                                <Button
                                    type="button"
                                    className={`btn btn--cta border ${
                                        answerType === "single" ||
                                        (!pendingType &&
                                            questions?.[index]
                                                ?.question_type === "single")
                                            ? "border-cta"
                                            : "!bg-white border-black"
                                    }`}
                                    size="sm"
                                    onClick={() => setAnswerType("single")}
                                    disabled={isReadOnly}
                                >
                                    Single
                                </Button>
                                <Button
                                    type="button"
                                    className={`btn btn--cta border ${
                                        answerType === "multiple" ||
                                        (!pendingType &&
                                            questions?.[index]
                                                ?.question_type === "multiple")
                                            ? "border-cta"
                                            : "!bg-white border-black"
                                    }`}
                                    size="sm"
                                    onClick={() => setAnswerType("multiple")}
                                    disabled={isReadOnly}
                                >
                                    Multiple
                                </Button>
                            </div>
                        </div>

                        {!!(
                            pendingType === "freeform" ||
                            (!pendingType && questions?.[index]?.question_type === "freeform")
                        ) && (
                            <div className="flex justify-between border border-gray-400 p-2 border-1 rounded-md">
                                <div className="basis-5/6">
                                    <Typography
                                        variant="h5"
                                        className="text-black"
                                    >
                                        Gobby&#8482; &lsquo;matched&lsquo; responses
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-body text-black"
                                    >
                                        Allow &lsquo;matched&rsquo; e.g., view and match with
                                        relevant Gobby responses to their own,
                                        for a more interactive peer experience.
                                        Turn this option off where responses
                                        might include personally identifiable or
                                        highly sensitive information.
                                    </Typography>
                                    {questionPII && (
                                        <Typography
                                            variant="paragraph"
                                            color="orange"
                                            className="font-body text-orange-500"
                                        >
                                            This option is automatically disabled when
                                            PII is enabled to protect sensitive
                                            information.
                                        </Typography>
                                    )}
                                </div>
                                <div className="place-self-center">
                                    <Switch
                                        checked={respondentsSeeOthers}
                                        color="orange"
                                        id="checkbox3"
                                        text=""
                                        onChange={() => handleCheckViewOthers()}
                                        disabled={isReadOnly || questionPII}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="flex flex-col md:flex-row md:items-start gap-4 border border-gray-400 p-2 rounded-md">
                            <div className="flex-1">
                                <Typography variant="h5" className="text-black">
                                    Add a question description (optional)
                                </Typography>
                                <Typography variant="paragraph" color="black">
                                    Add optional text directly underneath this
                                    question to support context and guidance for
                                    respondents.
                                </Typography>
                            </div>
                            <div className="place-self-center">
                                <Switch
                                    checked={questionDescription}
                                    color="orange"
                                    id="checkbox4"
                                    text=""
                                    onChange={() => handleCheckDescription()}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                        {!!questionDescription && (
                            <RHFTextarea
                                name={`questions.${index}.description`}
                                label="Question Description"
                                className="text-lg font-body text-primary-text"
                                autoFocus={false}
                                tabIndex="-1"
                            />
                        )}

                        {!!(
                            pendingType === "mixed" ||
                            pendingType === "freeform" ||
                            (!pendingType && questions?.[index]?.question_type === "mixed") ||
                            (!pendingType && questions?.[index]?.question_type === "freeform")
                        ) && (
                            <div className="flex justify-between border border-gray-400 p-2 border-1 rounded-md">
                                <div>
                                    <Typography
                                        variant="h5"
                                        className="text-black"
                                    >
                                        Gobby&#8482; response character limit
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-body text-black"
                                    >
                                        Sets the character limit to a
                                        Gobby&#8482; response. A maximum of 200
                                        characters can be applied. Where
                                        &apos;Multiple&apos; responses are allowed,
                                        respondents can save more than one
                                        answer.
                                    </Typography>
                                </div>
                                <div className="place-self-center border rounded border-black-500 text-center text-black">
                                    <RHFInput
                                        className="p-2 font-body text-center"
                                        type="number"
                                        variant="static"
                                        size="md"
                                        color="black"
                                        min="1"
                                        max="200"
                                        name={`questions.${index}.answer_limit`}
                                        placeholder="enter a value"
                                        disabled={isReadOnly}
                                        // The resolver runs raw (build.js),
                                        // so the number cast must happen
                                        // at input time or the payload
                                        // ships a string. A cleared input
                                        // maps to undefined, which
                                        // JSON.stringify drops from the
                                        // payload, so the backend's
                                        // exists-guard keeps the prior
                                        // value instead of persisting a
                                        // new null.
                                        registerOptions={{
                                            setValueAs: (v) =>
                                                v === "" ? undefined : Number(v),
                                        }}
                                    />
                                    characters
                                </div>
                            </div>
                        )}
                    </div>
                    <p></p>
                </DialogBody>

                <DialogFooter className="dialog__footer">
                    <Button
                        type="button"
                        variant="filled"
                        className="btn btn--primary"
                        onClick={() => handleApply()}
                        disabled={isReadOnly}
                    >
                        Apply
                    </Button>
                </DialogFooter>
            </Dialog>

            <Dialog
                open={showPresetRemovalDialog}
                handler={closeOnly}
                dismiss={{ outsidePress: false }}
                className="dialog"
            >
                <DialogHeader className="dialog__header">
                    <p>Changing type?</p>
                </DialogHeader>
                <DialogBody divider className="dialog__body">
                    {
                        "Are you sure? This will permanently remove the options for this question."
                    }
                </DialogBody>
                <DialogFooter className="dialog__footer gap-2">
                    <Button
                        type="button"
                        variant="filled"
                        className="btn btn--secondary"
                        onClick={closeOnly}
                    >
                            Cancel
                    </Button>
                    <Button
                        type="button"
                        className="btn btn--danger"
                        variant="filled"
                        onClick={onConfirmDeletion}
                    >
                            Yes
                    </Button>
                </DialogFooter>
            </Dialog>
        </>
    );
}
